import type { DialogRef } from '@myosh/odin-components';
import { OdinIcon, OdinIconSize } from '@myosh/odin-components';
import { memo, useCallback, useEffect, useRef } from 'react';
import { Observable, skipWhile, Subject, take, takeUntil } from 'rxjs';
import type { RecordResult } from '../../@types/records';
import type { OdinDBRecordMutationMetadata } from '../../db/odin-db-types';
import { isDbRecordId } from '../../db/odin-db-util';
import { useOnlineStatus } from '../../hooks/use-online-status';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { syncOfflineMutation } from '../../redux/middleware/offline-mutations.thunk';
import { useDeleteDbRecordMutation } from '../../redux/services/db-api';
import { selectIsSyncing } from '../../redux/slices/offline-sync-slice';
import ConfirmationDialog from '../common/confirmation-dialog.component';

interface RecordOfflineProps {
  recordId?: string | number;
  metadata?: OdinDBRecordMutationMetadata;
  saveRecord: () => Observable<RecordResult> | undefined;
  formIsDirty: () => boolean | undefined;
  close: () => void;
}

const RecordOffline = ({ recordId, metadata, saveRecord, formIsDirty, close }: RecordOfflineProps) => {
  const saveConfirmationModalRef = useRef<DialogRef>(null);
  const deleteConfirmationModalRef = useRef<DialogRef>(null);
  const destroySubject = useRef(new Subject<void>());

  const [deleteDbRecord] = useDeleteDbRecordMutation();

  const dispatch = useAppDispatch();
  const isSyncing = useAppSelector(selectIsSyncing);

  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (metadata?.state === 'synced') {
      close();
    }
  }, [metadata?.state, close]);

  const handleShowDeleteConfirmation = useCallback(() => {
    deleteConfirmationModalRef.current?.show();
  }, []);

  const handleDeleteClick = useCallback(async () => {
    if (recordId && isDbRecordId(recordId)) {
      await deleteDbRecord(recordId);
      close();
    }
  }, [recordId, deleteDbRecord, close]);

  const handleSyncClick = useCallback(() => {
    if (recordId && isDbRecordId(recordId)) {
      saveRecord?.()
        ?.pipe(
          skipWhile((result) => !result || Object.keys(result).length === 0),
          take(1),
          takeUntil(destroySubject.current)
        )
        .subscribe(() => {
          dispatch(syncOfflineMutation(recordId));
        });
    }
  }, [dispatch, recordId, saveRecord]);

  const handleShowSaveConfirmation = useCallback(() => {
    if (formIsDirty && formIsDirty()) {
      saveConfirmationModalRef.current?.show();
    } else {
      handleSyncClick();
    }
  }, [formIsDirty, handleSyncClick]);

  if (!metadata) {
    return null;
  }

  return (
    <>
      <div className="mt-1 flex flex-col">
        {metadata.state === 'error' && metadata.error && (
          <div className="mr-2 flex flex-row rounded-md border border-error p-2 text-xs">
            <OdinIcon icon="CloudAlert" size={OdinIconSize.ExtraSmall} className="text-error" />
            <span className="ml-1">Unable to sync record: {metadata.error}</span>
          </div>
        )}

        {metadata.state === 'conflict' && (
          <div className="mr-2 flex flex-row rounded-md border border-warning p-2 text-xs">
            <OdinIcon icon="CloudAlert" size={OdinIconSize.ExtraSmall} className="text-warning" />
            <span className="ml-1">
              Looks like someone else tried to update this record. Please open the conflict modal to resolve any
              conflicts.
            </span>
          </div>
        )}

        <div className="mt-1 flex flex-row gap-4">
          {isSyncing && (
            <div className="flex flex-row items-center gap-1 whitespace-nowrap text-sm leading-5">
              <OdinIcon size={OdinIconSize.ExtraSmall} icon="Loader" spin />
              Syncing...
            </div>
          )}
          {isOnline && !isSyncing && (
            <button
              className="flex cursor-pointer flex-row items-center gap-1 whitespace-nowrap text-sm leading-5"
              title="Sync local record"
              onClick={handleShowSaveConfirmation}
            >
              <OdinIcon
                icon="CloudUpload"
                size={OdinIconSize.ExtraSmall}
                className="text-primary-2 hover:text-primary-1"
              />
              Sync
            </button>
          )}
          {!isSyncing && (
            <button
              className="flex cursor-pointer flex-row items-center gap-1 whitespace-nowrap text-sm leading-5"
              title="Delete local record"
              onClick={handleShowDeleteConfirmation}
            >
              <OdinIcon icon="Trash" size={OdinIconSize.ExtraSmall} className="text-primary-2 hover:text-primary-1" />
              Delete
            </button>
          )}
        </div>
      </div>
      <ConfirmationDialog ref={saveConfirmationModalRef} onConfirm={handleSyncClick} hasCancelButton />
      <ConfirmationDialog
        ref={deleteConfirmationModalRef}
        header="Confirm Delete"
        onConfirm={handleDeleteClick}
        confirmButtonText="Delete"
        hasCancelButton
      >
        Are you sure you want to delete the local record? This action cannot be undone.
      </ConfirmationDialog>
    </>
  );
};

export default memo(RecordOffline);
